import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Link,
  InputAdornment,
  Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Email as EmailIcon, Lock as LockIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import BrandWordmark, { CHECK_NAVY, NOW_GRADIENT_TOP, NOW_GRADIENT_BOTTOM } from '../components/BrandWordmark';
import LoginBackground, { LoginBrandPanelDecor } from '../components/LoginBackground';

type AppUserRole = 'Admin' | 'Owner' | 'Super Admin' | 'Team Leader' | 'Manager' | 'User';

interface LoginProps {
  onLogin: (user: { role: AppUserRole; token: string; businessName?: string }) => void;
}

/** Black tick — use on white/light backgrounds */
const LOGO_LIGHT_BG = `${process.env.PUBLIC_URL}/safety.png`;
/** White tick, transparent background — for navy brand panel */
const LOGO_DARK_BG = `${process.env.PUBLIC_URL}/safety-dark.png?v=2`;

const logoTileSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  bgcolor: '#ffffff',
  boxShadow: `0 8px 32px ${alpha(CHECK_NAVY, 0.25)}`,
  border: `1px solid ${alpha('#ffffff', 0.2)}`,
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 1.5,
    bgcolor: '#ffffff',
    fontSize: '0.9375rem',
    transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
    '& fieldset': {
      borderColor: alpha(CHECK_NAVY, 0.14),
    },
    '&:hover fieldset': {
      borderColor: alpha(CHECK_NAVY, 0.28),
    },
    '&.Mui-focused fieldset': {
      borderColor: NOW_GRADIENT_TOP,
      borderWidth: 1,
    },
    '&.Mui-focused': {
      boxShadow: `0 0 0 3px ${alpha(NOW_GRADIENT_TOP, 0.15)}`,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: NOW_GRADIENT_BOTTOM,
  },
};

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailError, setResetEmailError] = useState('');
  const [resetEmailSuccess, setResetEmailSuccess] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/auth/login`, {
        email,
        password,
      });

      const { token, user } = response.data;
      const { role, businessName, businessId } = user;

      if (businessName) {
        localStorage.setItem('businessName', businessName);
      }
      if (businessId) {
        localStorage.setItem('businessId', businessId);
      }

      onLogin({ role, token, businessName });
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.error === 'string'
          ? err.response.data.error
          : 'Invalid email or password. Please try again.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail || !/\S+@\S+\.\S+/.test(resetEmail)) {
      setResetEmailError('Please enter a valid email address');
      return;
    }

    setResetLoading(true);
    setResetEmailError('');
    setResetEmailSuccess(false);

    try {
      const response = await axios.post(`${API_BASE_URL}/auth/forgot-password`, {
        email: resetEmail,
      });

      if (response.data.success) {
        setResetEmailSuccess(true);
        setResetEmail('');
        setTimeout(() => {
          setForgotPasswordOpen(false);
          setResetEmailSuccess(false);
        }, 3000);
      } else {
        setResetEmailError(response.data.message || 'Failed to send reset email');
      }
    } catch (err: unknown) {
      const message =
        axios.isAxiosError(err) && typeof err.response?.data?.message === 'string'
          ? err.response.data.message
          : 'An error occurred while sending the reset email';
      setResetEmailError(message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleCloseModal = () => {
    setForgotPasswordOpen(false);
    setResetEmail('');
    setResetEmailError('');
    setResetEmailSuccess(false);
  };

  const signInButtonSx = {
    mt: 3,
    mb: 1,
    py: 1.35,
    borderRadius: 2,
    fontWeight: 600,
    textTransform: 'none' as const,
    fontSize: '0.9375rem',
    letterSpacing: '0.01em',
    background: `linear-gradient(135deg, ${NOW_GRADIENT_TOP} 0%, ${NOW_GRADIENT_BOTTOM} 100%)`,
    boxShadow: `0 2px 8px ${alpha(NOW_GRADIENT_BOTTOM, 0.28)}`,
    '&:hover': {
      background: `linear-gradient(135deg, ${NOW_GRADIENT_TOP} 0%, ${CHECK_NAVY} 100%)`,
      boxShadow: `0 4px 14px ${alpha(NOW_GRADIENT_BOTTOM, 0.35)}`,
    },
    '&.Mui-disabled': {
      background: alpha(NOW_GRADIENT_BOTTOM, 0.4),
      color: alpha('#ffffff', 0.9),
    },
  };

  const formContent = (
    <>
      <Box sx={{ mb: 3, display: { xs: 'none', md: 'block' } }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: CHECK_NAVY,
            fontFamily: "'Montserrat', sans-serif",
            letterSpacing: '-0.02em',
            mb: 0.75,
          }}
        >
          Sign in
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          Enter your credentials to access the admin portal.
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          textAlign: 'center',
          display: { xs: 'block', md: 'none' },
        }}
      >
        <Box
          sx={{
            ...logoTileSx,
            width: 72,
            height: 72,
            borderRadius: 3,
            mb: 2,
            boxShadow: `0 4px 20px ${alpha(CHECK_NAVY, 0.08)}`,
            border: `1px solid ${alpha(CHECK_NAVY, 0.06)}`,
          }}
        >
          <Box
            component="img"
            src={LOGO_LIGHT_BG}
            alt="Check-Now"
            sx={{ width: 52, height: 52, objectFit: 'contain' }}
          />
        </Box>
        <BrandWordmark variant="h5" component="h1" />
        <Typography
          variant="overline"
          sx={{
            display: 'block',
            mt: 1,
            color: 'text.secondary',
            letterSpacing: '0.14em',
            fontSize: '0.68rem',
            fontWeight: 600,
          }}
        >
          Admin portal
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <TextField
          margin="dense"
          required
          fullWidth
          id="email"
          label="Email address"
          name="email"
          autoComplete="email"
          autoFocus
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          variant="outlined"
          disabled={submitting}
          sx={{ ...inputSx, mt: 0 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ color: alpha(CHECK_NAVY, 0.45), fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="Password"
          type="password"
          id="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          variant="outlined"
          disabled={submitting}
          sx={inputSx}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LockIcon sx={{ color: alpha(CHECK_NAVY, 0.45), fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          size="large"
          disabled={submitting}
          disableElevation
          sx={signInButtonSx}
        >
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Sign in'}
        </Button>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Link
            component="button"
            type="button"
            variant="body2"
            onClick={() => setForgotPasswordOpen(true)}
            sx={{
              color: NOW_GRADIENT_BOTTOM,
              fontWeight: 500,
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontFamily: 'inherit',
              '&:hover': { color: NOW_GRADIENT_TOP },
            }}
          >
            Forgot password?
          </Link>
        </Box>
      </Box>
    </>
  );

  return (
    <>
      <link
        href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Roboto:wght@400;500;700&display=swap"
        rel="stylesheet"
      />

      <Box sx={{ minHeight: '100vh', display: 'flex' }}>
        {/* Brand panel — desktop */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            width: { md: '42%', lg: '40%' },
            minHeight: '100vh',
            bgcolor: CHECK_NAVY,
            px: { md: 5, lg: 6 },
            py: 5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <LoginBrandPanelDecor />
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
            }}
          >
            <Box
              component="img"
              src={LOGO_DARK_BG}
              alt="Check-Now"
              sx={{
                width: 56,
                height: 56,
                objectFit: 'contain',
                flexShrink: 0,
                display: 'block',
              }}
            />
            <BrandWordmark variant="h3" component="p" onDark fontWeight={800} />
          </Box>
          <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 360 }}>
            <Typography
              variant="body1"
              sx={{
                color: alpha('#ffffff', 0.82),
                lineHeight: 1.7,
                fontWeight: 400,
              }}
            >
              Manage checks, assets, and compliance from one secure admin portal.
            </Typography>
          </Box>
          <Typography
            variant="caption"
            sx={{ position: 'relative', zIndex: 1, color: alpha('#ffffff', 0.35) }}
          >
            © {new Date().getFullYear()} Check-Now
          </Typography>
        </Box>

        {/* Form panel */}
        <Box
          component="main"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            minHeight: '100vh',
            px: { xs: 2, sm: 3 },
            py: { xs: 4, md: 5 },
          }}
        >
          <LoginBackground />
          <Paper
            elevation={0}
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: 400,
              p: { xs: 3.5, sm: 4 },
              borderRadius: 3,
              bgcolor: alpha('#ffffff', 0.92),
              backdropFilter: 'blur(12px)',
              border: `1px solid ${alpha(CHECK_NAVY, 0.08)}`,
              boxShadow: `0 1px 2px ${alpha(CHECK_NAVY, 0.04)}, 0 12px 40px ${alpha(CHECK_NAVY, 0.06)}`,
            }}
          >
            {formContent}
          </Paper>

          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              mt: 3,
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Link
              href="https://check-now.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: NOW_GRADIENT_BOTTOM },
              }}
            >
              Learn more
            </Link>
            <Divider orientation="vertical" flexItem sx={{ height: 14, alignSelf: 'center' }} />
            <Link
              component={RouterLink}
              to="/support"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: NOW_GRADIENT_BOTTOM },
              }}
            >
              Support
            </Link>
            <Divider orientation="vertical" flexItem sx={{ height: 14, alignSelf: 'center' }} />
            <Link
              component={RouterLink}
              to="/privacy-policy"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: NOW_GRADIENT_BOTTOM },
              }}
            >
              Privacy
            </Link>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 24px 48px ${alpha(CHECK_NAVY, 0.12)}`,
            border: `1px solid ${alpha(CHECK_NAVY, 0.08)}`,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: CHECK_NAVY, pb: 0, fontFamily: "'Montserrat', sans-serif" }}>
          Reset password
        </DialogTitle>
        <DialogContent>
          {resetEmailSuccess ? (
            <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
              Password reset email sent. Please check your inbox.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2, lineHeight: 1.6 }}>
                Enter your email and we&apos;ll send you a link to reset your password.
              </Typography>

              {resetEmailError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {resetEmailError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Email address"
                variant="outlined"
                margin="dense"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoFocus
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: alpha(CHECK_NAVY, 0.45), fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          {!resetEmailSuccess && (
            <>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                disabled={resetLoading}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  borderColor: alpha(CHECK_NAVY, 0.2),
                  color: CHECK_NAVY,
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleForgotPassword}
                disabled={resetLoading}
                disableElevation
                sx={{ ...signInButtonSx, mt: 0, mb: 0, minWidth: 140 }}
              >
                {resetLoading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : 'Send link'}
              </Button>
            </>
          )}
          {resetEmailSuccess && (
            <Button variant="contained" onClick={handleCloseModal} disableElevation sx={{ ...signInButtonSx, mt: 0, mb: 0 }}>
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Login;
